import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContextMenu, { MenuItems, MenuItem as SBMenuItem } from '@sendbird/uikit-react/ui/ContextMenu';
import Icon, { IconTypes, IconColors } from '@sendbird/uikit-react/ui/Icon';
import SBIconButton from '@sendbird/uikit-react/ui/IconButton';
import ChannelAvatar from '@sendbird/uikit-react/ui/ChannelAvatar';
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import { alpha, styled } from '@mui/material/styles';
import { useIntl, type IntlShape } from 'react-intl';

import Tooltip from 'components/mui/Tooltip';
import TruncatedTooltip from 'components/mui/Tooltip/TruncatedTooltip';
import { userProfileSelector } from 'domain/env';
import { chatParticipantsSelector } from 'domain/chat';
import { companiesByIdSelector } from 'domain/companies';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { ChannelContext } from '../../types';
import { getChannelTitle, resolveChannelContext } from '../../helpers';
import EditChannelModal from '../EditChannelModal';
import { UnreadCountBadge } from './StyledComponents';

interface ChannelPreviewItemProps {
  channel: GroupChannel;
  isSelected: boolean;
  onClick: () => void;
  onPreviewDocument: (companyId: string, documentId: string) => void;
  onDeleteChannel: (channelUrl: string) => void;
  /**
   * Mobile view — renders the company name as plain text instead of a new-tab
   * link (opening the app in a second tab lands on the "Browser is not
   * supported" page in mobile view).
   */
  isMobile?: boolean;
}

// The channel list lives inside the chat Drawer modal (zIndex 1600), so a default Tooltip
// Popper (theme.zIndex.tooltip = 1500) renders behind the drawer and is invisible. Lift the
// Popper above the drawer (1700), matching the chat header action-button tooltips
// (`headerTooltipSlotProps` in `ChatHeaderView.tsx`).
const tooltipSlotProps = { popper: { sx: { zIndex: 1700 } } } as const;

// Lifted out so it's not re-created per render. Uses the active react-intl locale via
// `formatDate`/`formatTime`, so month names follow the user's language instead of being
// hard-coded English (Jan/Feb/...).
const formatTimestamp = (intl: IntlShape, ts: number): string => {
  if (!ts) return '';
  const date = new Date(ts);
  const now = new Date();
  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  return isToday
    ? intl.formatTime(date, { hour: '2-digit', minute: '2-digit' })
    : intl.formatDate(date, { month: 'short', day: 'numeric' });
};

const getLastMessageText = (intl: IntlShape, channel: GroupChannel): string => {
  const msg = channel.lastMessage;
  if (!msg) return '';
  if ('message' in msg && typeof msg.message === 'string') return msg.message;
  if (msg.messageType === 'file') {
    return intl.formatMessage({
      id: 'chat.channelList.lastMessage.file',
      defaultMessage: 'File',
    });
  }
  return '';
};

// `styled` (computed once) over `sx` (re-serialized every render) — this is a list-row
// hot path, per `src/components/CLAUDE.md`. `shouldForwardProp` keeps the booleans off the
// DOM (Emotion has no transient-prop syntax).
const RowRoot = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' && prop !== 'menuOpen' && prop !== 'canManage',
})<{ isSelected: boolean; menuOpen: boolean; canManage: boolean }>(
  ({ theme, isSelected, menuOpen, canManage }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(2.375),
    cursor: 'pointer',
    borderLeft: isSelected
      ? `4px solid ${theme.palette.primary.main}`
      : '4px solid transparent',
    // Shared bottom divider — identical color/width to user rows and the empty "No chats" block so
    // the combined Chats + Users list reads as one continuously separated list (DA-19646, task 6.2).
    borderBottom: `1px solid ${theme.palette.divider}`,
    // When menu is open, keep the row in its hover background and reveal the menu button +
    // hide the timestamp — Sendbird's dropdown renders in a portal so :hover is lost as soon
    // as the cursor moves to a menu item; menuOpen mirrors what setSupposedHover does on the
    // message hover menu (see MessageItemMenu in @sendbird/uikit-react).
    backgroundColor: isSelected
      ? alpha(theme.palette.primary.main, 0.12)
      : menuOpen
        ? theme.palette.action.hover
        : 'transparent',
    '& .channel-preview-menu-btn': { display: menuOpen ? 'flex' : 'none' },
    '&:hover .channel-preview-menu-btn': { display: 'flex' },
    '& .channel-preview-timestamp': canManage && menuOpen ? { display: 'none' } : {},
    '&:hover .channel-preview-timestamp': canManage ? { display: 'none' } : {},
    // When the 3-dot menu button is showing it sits absolutely at the row's right
    // edge (32px wide). The timestamp is hidden at the same time, which would let
    // the `flexShrink: 1` channel name grow under the button and overlap it. Reserve
    // the button's width (plus the row gap) as right padding on the name row so the
    // title truncates *before* the button instead of sliding beneath it.
    '& .channel-preview-name-row': canManage && menuOpen ? { paddingRight: theme.spacing(4.5) } : {},
    '&:hover .channel-preview-name-row': canManage ? { paddingRight: theme.spacing(4.5) } : {},
    '&:hover': {
      backgroundColor: isSelected
        ? alpha(theme.palette.primary.main, 0.12)
        : theme.palette.action.hover,
    },
  }),
);

const ChannelPreviewItem: React.FC<ChannelPreviewItemProps> = ({
  channel,
  isSelected,
  onClick,
  onPreviewDocument,
  onDeleteChannel,
  isMobile = false,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const userProfile = useSelector(userProfileSelector);
  const chatParticipants = useSelector(chatParticipantsSelector);
  const companies = useSelector(companiesByIdSelector);
  const isMultiCompany = companies.size > 1;
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingChannelUrl, setEditingChannelUrl] = useState<string | null>(null);
  const isCreator = channel.creator?.userId === userProfile.id;
  const isOperator = channel.myRole === 'operator';
  const canManage = isCreator || isOperator;

  const ctx: ChannelContext | null = resolveChannelContext(channel);
  const hasDocumentId = Boolean(ctx?.documentId && ctx?.companyId);
  const hasCompanyId = Boolean(ctx?.companyId);
  const isAP = ctx?.app === 'AP';

  const channelName = getChannelTitle(
    channel,
    userProfile.id,
    chatParticipants,
    formatMessage({ id: 'chat.channelList.unnamedChat', defaultMessage: 'Group name' }),
  );
  const lastMessageText = getLastMessageText(intl, channel);
  const timestamp = channel.lastMessage?.createdAt || channel.createdAt;
  const unreadCount = channel.unreadMessageCount || 0;

  const companyName = ctx?.companyId ? (companies.get(ctx.companyId)?.cname || null) : null;
  const companyPath =
    ctx?.companyId && isAP
      ? `${generatePath(ROUTES_PATH.COMPANY_WORKSPACE_CATEGORY_UNDEFINED.absolute, { companyId: ctx.companyId })}?chatChannel=${encodeURIComponent(channel.url)}`
      : null;

  return (
    <RowRoot
      onClick={onClick}
      isSelected={isSelected}
      menuOpen={menuOpen}
      canManage={canManage}
    >
      {/* Avatar — 56x56 per Figma, vertically centered */}
      <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
        <ChannelAvatar channel={channel} width={56} height={56} userId={userProfile.id} theme="light" />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Rows 1+2: Channel name + company (grouped with 4px gap) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, position: 'relative' }}>
          {/* Row 1: Channel name + eye icon + timestamp */}
          <Box
            className="channel-preview-name-row"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <TruncatedTooltip title={channelName} placement="bottom" disableInteractive>
              <Typography
                variant="subtitle2"
                noWrap
                sx={(theme) => ({
                  flexShrink: 1,
                  minWidth: 0,
                  lineHeight: '16px',
                  color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                })}
              >
                {channelName}
              </Typography>
            </TruncatedTooltip>
            {hasDocumentId && (
              <Tooltip
                title={formatMessage({
                  id: 'chat.previewDocument',
                  defaultMessage: 'Preview document',
                })}
                disableInteractive
                slotProps={tooltipSlotProps}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreviewDocument(String(ctx!.companyId), String(ctx!.documentId));
                  }}
                  sx={{ flexShrink: 0, p: 0 }}
                >
                  <VisibilityOutlinedIcon sx={{ fontSize: 16, color: 'action.active' }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography
              className="channel-preview-timestamp"
              variant="caption"
              noWrap
              sx={{ flexShrink: 0, ml: 'auto', lineHeight: '16px', color: 'text.disabled' }}
            >
              {formatTimestamp(intl, timestamp)}
            </Typography>
          </Box>
          {/* 3-dot context menu — absolutely positioned over timestamp, no layout shift */}
          {canManage && (
            <Box
              className="channel-preview-menu-btn"
              sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ContextMenu
                isOpen={menuOpen}
                menuTrigger={() => (
                  <SBIconButton
                    ref={menuButtonRef}
                    className="channel-preview-menu__trigger"
                    width="32px"
                    height="32px"
                    onClick={() => setMenuOpen((prev) => !prev)}
                  >
                    <Icon
                      type={IconTypes.MORE}
                      fillColor={IconColors.ON_BACKGROUND_3}
                      width="22px"
                      height="22px"
                    />
                  </SBIconButton>
                )}
                menuItems={() => (
                  <MenuItems
                    parentRef={menuButtonRef}
                    closeDropdown={() => setMenuOpen(false)}
                    openLeft
                  >
                    <SBMenuItem
                      className="channel-preview-menu__edit-item"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        setEditingChannelUrl(channel.url);
                      }}
                    >
                      {formatMessage({ id: 'chat.channelList.editChannel', defaultMessage: 'Edit channel' })}
                    </SBMenuItem>
                    <SBMenuItem
                      className="channel-preview-menu__delete-item"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setMenuOpen(false);
                        onDeleteChannel(channel.url);
                      }}
                    >
                      {formatMessage({ id: 'chat.channelList.deleteChat', defaultMessage: 'Delete chat' })}
                    </SBMenuItem>
                  </MenuItems>
                )}
              />
            </Box>
          )}

          {/* Row 2: Company name link (multi-company users only) */}
          {isMultiCompany && hasCompanyId && companyName && (
            <Typography variant="caption" noWrap sx={{ lineHeight: '16px', color: 'text.secondary' }}>
              {companyPath && !isMobile ? (
                <Link
                  href={companyPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  color="inherit"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  {companyName}
                </Link>
              ) : (
                companyName
              )}
            </Typography>
          )}
        </Box>

        {/* Row 3: Last message preview (+ unread count badge), or "No messages yet" placeholder */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="caption"
            noWrap
            sx={(theme) => ({
              flex: 1,
              minWidth: 0,
              lineHeight: '16px',
              color: isSelected ? theme.palette.text.primary : theme.palette.text.disabled,
            })}
          >
            {lastMessageText ||
              formatMessage({ id: 'chat.channelList.noMessagesYet', defaultMessage: 'No messages yet' })}
          </Typography>
          {unreadCount > 0 && <UnreadCountBadge>{unreadCount > 99 ? '99+' : unreadCount}</UnreadCountBadge>}
        </Box>
      </Box>

      {editingChannelUrl && (
        <EditChannelModal
          channelUrl={editingChannelUrl}
          onClose={() => setEditingChannelUrl(null)}
        />
      )}
    </RowRoot>
  );
};

export default ChannelPreviewItem;
